import { useState, useEffect, useCallback } from 'react';
import { useStore } from '@nanostores/react';
import { chatId } from '~/lib/persistence/useChatHistory';
import { apiGetStaticDeploymentByChatId, type StaticDeployment } from '~/lib/api/deployments';
import {
  apiGetFormSubmissions,
  apiDeleteFormSubmission,
  apiExportFormSubmissions,
  apiGetFormIds,
  apiGetFormSettings,
  apiSaveFormSettings,
  downloadCsv,
  type FormSubmission,
  type FormSettings,
  type EmailProviderType,
} from '~/lib/api/forms';
import { classNames } from '~/utils/classNames';
import { ConfirmationDialog } from '~/components/ui/Dialog';

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getFieldPreview(fields: Record<string, unknown>): string {
  const email = fields.email || fields.Email;
  const name = fields.name || fields.Name;

  if (email && name) {
    return `${name} (${email})`;
  }

  if (email) {
    return String(email);
  }

  if (name) {
    return String(name);
  }

  const firstKey = Object.keys(fields)[0];

  return firstKey ? String(fields[firstKey]).slice(0, 50) : 'No data';
}

const EMAIL_PROVIDERS: { value: EmailProviderType; label: string; needsDomain: boolean }[] = [
  { value: 'sendgrid', label: 'SendGrid', needsDomain: false },
  { value: 'mailgun', label: 'Mailgun', needsDomain: true },
  { value: 'postmark', label: 'Postmark', needsDomain: false },
  { value: 'brevo', label: 'Brevo', needsDomain: false },
  { value: 'resend', label: 'Resend', needsDomain: false },
];

type TabType = 'submissions' | 'settings';

export function FormsPanel() {
  const currentChatId = useStore(chatId);
  const [deployment, setStaticDeployment] = useState<StaticDeployment | null>(null);
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Form selection
  const [formIds, setFormIds] = useState<string[]>([]);
  const [selectedFormId, setSelectedFormId] = useState<string>('');

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('submissions');

  // Settings state
  const [settings, setSettings] = useState<Partial<FormSettings>>({});
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);

  const loadSubmissions = useCallback(async (deploymentId: string, formId?: string) => {
    try {
      const result = await apiGetFormSubmissions(deploymentId, { formId: formId || undefined });
      setSubmissions(result.submissions);
      setTotal(result.total);
    } catch {
      setSubmissions([]);
      setTotal(0);
    }
  }, []);

  const loadFormIds = useCallback(
    async (deploymentId: string) => {
      try {
        const ids = await apiGetFormIds(deploymentId);
        setFormIds(ids);

        if (ids.length > 0 && !selectedFormId) {
          setSelectedFormId(ids[0]);
        }
      } catch {
        setFormIds([]);
      }
    },
    [selectedFormId],
  );

  const loadSettings = useCallback(async (deploymentId: string, formId: string) => {
    if (!formId) {
      return;
    }

    setLoadingSettings(true);

    try {
      const result = await apiGetFormSettings(deploymentId, formId);
      setSettings(result);
    } catch {
      setSettings({});
    } finally {
      setLoadingSettings(false);
    }
  }, []);

  useEffect(() => {
    if (!currentChatId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    apiGetStaticDeploymentByChatId(currentChatId).then((dep) => {
      setStaticDeployment(dep);

      if (dep?.id) {
        Promise.all([loadSubmissions(dep.id), loadFormIds(dep.id)]).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });
  }, [currentChatId, loadSubmissions, loadFormIds]);

  // Reload submissions when form selection changes
  useEffect(() => {
    if (deployment?.id && selectedFormId) {
      loadSubmissions(deployment.id, selectedFormId);
    }
  }, [deployment?.id, selectedFormId, loadSubmissions]);

  // Load settings when switching to settings tab or changing form
  useEffect(() => {
    if (deployment?.id && selectedFormId && activeTab === 'settings') {
      loadSettings(deployment.id, selectedFormId);
    }
  }, [deployment?.id, selectedFormId, activeTab, loadSettings]);

  const handleExport = async () => {
    if (!deployment) {
      return;
    }

    setExporting(true);

    try {
      const result = await apiExportFormSubmissions(deployment.id, selectedFormId || undefined);
      downloadCsv(result.csv, result.filename);
    } catch {
      // Silent fail
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async (submissionId: string) => {
    if (!deployment) {
      return;
    }

    try {
      await apiDeleteFormSubmission(deployment.id, submissionId);
      setSubmissions((prev) => prev.filter((s) => s._id !== submissionId));
      setTotal((prev) => prev - 1);
      setDeleteConfirm(null);
    } catch {
      // Silent fail
    }
  };

  const handleSaveSettings = async () => {
    if (!deployment || !selectedFormId) {
      return;
    }

    setSavingSettings(true);
    setSettingsSaved(false);

    try {
      await apiSaveFormSettings(deployment.id, selectedFormId, settings);
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 2000);
    } catch {
      // Silent fail
    } finally {
      setSavingSettings(false);
    }
  };

  const updateSettings = <K extends keyof FormSettings>(key: K, value: FormSettings[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full p-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="i-ph:envelope-simple text-lg text-[#D9F26A]" />
          <h3 className="text-sm font-semibold text-primary">Forms</h3>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="i-svg-spinners:90-ring-with-bg text-xl text-muted" />
        </div>
      </div>
    );
  }

  if (!deployment) {
    return (
      <div className="flex flex-col h-full p-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="i-ph:envelope-simple text-lg text-[#D9F26A]" />
          <h3 className="text-sm font-semibold text-primary">Forms</h3>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <div className="w-12 h-12 rounded-xl bg-raised flex items-center justify-center mb-4">
            <div className="i-ph:rocket-launch text-2xl text-muted" />
          </div>
          <h4 className="text-sm font-medium text-primary mb-1">Deploy first</h4>
          <p className="text-xs text-muted max-w-[240px] leading-relaxed">
            Deploy your landing page to start collecting form submissions.
          </p>
        </div>
      </div>
    );
  }

  if (formIds.length === 0 && submissions.length === 0) {
    return (
      <div className="flex flex-col h-full p-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="i-ph:envelope-simple text-lg text-[#D9F26A]" />
          <h3 className="text-sm font-semibold text-primary">Forms</h3>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <div className="w-12 h-12 rounded-xl bg-[#D9F26A]/10 flex items-center justify-center mb-4">
            <div className="i-ph:envelope-simple text-2xl text-[#D9F26A]" />
          </div>
          <h4 className="text-sm font-medium text-primary mb-1">No submissions yet</h4>
          <p className="text-xs text-muted max-w-[240px] leading-relaxed">
            Form submissions from your landing page will appear here.
          </p>
        </div>
      </div>
    );
  }

  const selectedProvider = EMAIL_PROVIDERS.find((p) => p.value === settings.emailProvider?.provider);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-hairline">
        <div className="flex items-center gap-2">
          <div className="i-ph:envelope-simple text-lg text-[#D9F26A]" />
          <h3 className="text-sm font-semibold text-primary">Forms</h3>
        </div>

        {/* Form selector */}
        {formIds.length > 0 && (
          <select
            value={selectedFormId}
            onChange={(e) => setSelectedFormId(e.target.value)}
            className="text-xs px-2 py-1.5 rounded-md bg-raised border border-hairline text-primary"
          >
            {formIds.map((id) => (
              <option key={id} value={id}>
                {id}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-hairline">
        <button
          onClick={() => setActiveTab('submissions')}
          className={classNames(
            'flex-1 px-4 py-2 text-xs font-medium transition-colors',
            activeTab === 'submissions'
              ? 'text-[#D9F26A] border-b-2 border-[#D9F26A]'
              : 'text-muted hover:text-primary',
          )}
        >
          Submissions ({total})
        </button>
        {/* Settings tab (email/webhook notifications) is hidden: per-app
            notification delivery is not wired up on the new full-stack backend
            yet — the endpoint returns null/400. Re-enable when it lands. */}
      </div>

      {/* Submissions Tab */}
      {activeTab === 'submissions' && (
        <>
          {/* Export button */}
          <div className="flex justify-end p-2 border-b border-hairline">
            <button
              onClick={handleExport}
              disabled={exporting || submissions.length === 0}
              className={classNames(
                'flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md transition-colors',
                'bg-raised hover:bg-sunken',
                'text-muted hover:text-primary',
                (exporting || submissions.length === 0) && 'opacity-50 cursor-not-allowed',
              )}
            >
              {exporting ? (
                <div className="i-svg-spinners:90-ring-with-bg text-sm" />
              ) : (
                <div className="i-ph:download-simple text-sm" />
              )}
              Export CSV
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-2">
            {submissions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <p className="text-xs text-muted">No submissions for this form.</p>
              </div>
            ) : (
              submissions.map((submission) => (
                <div key={submission._id} className="bg-raised rounded-lg border border-hairline overflow-hidden">
                  <button
                    onClick={() => setExpandedId(expandedId === submission._id ? null : submission._id)}
                    className="w-full flex items-center justify-between p-3 text-left hover:bg-sunken transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-[#D9F26A]/10 text-[#D9F26A]">
                          {submission.formId}
                        </span>
                        <span className="text-xs text-muted">{formatDate(submission.createdAt)}</span>
                      </div>
                      <p className="text-sm text-primary truncate">{getFieldPreview(submission.fields)}</p>
                    </div>
                    <div
                      className={classNames(
                        'i-ph:caret-down text-muted transition-transform',
                        expandedId === submission._id && 'rotate-180',
                      )}
                    />
                  </button>

                  {expandedId === submission._id && (
                    <div className="border-t border-hairline p-3 space-y-3">
                      <div className="space-y-2">
                        {Object.entries(submission.fields).map(([key, value]) => (
                          <div key={key} className="flex gap-2">
                            <span className="text-xs text-muted min-w-[80px]">{key}:</span>
                            <span className="text-xs text-primary break-all">
                              {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                            </span>
                          </div>
                        ))}
                      </div>

                      <div className="pt-2 border-t border-hairline flex items-center justify-between">
                        <div className="text-xs text-muted">
                          <span>{submission.meta.ip}</span>
                          {submission.meta.referrer !== 'direct' && (
                            <span className="ml-2">from {submission.meta.referrer}</span>
                          )}
                        </div>

                        <button
                          onClick={() => setDeleteConfirm(submission._id)}
                          className="text-xs text-muted hover:text-red-500 transition-colors"
                        >
                          <div className="i-ph:trash text-sm" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {loadingSettings ? (
            <div className="flex items-center justify-center py-8">
              <div className="i-svg-spinners:90-ring-with-bg text-xl text-muted" />
            </div>
          ) : !selectedFormId ? (
            <div className="text-center py-8">
              <p className="text-xs text-muted">Select a form to configure settings.</p>
            </div>
          ) : (
            <>
              {/* Email Provider */}
              <section className="space-y-3">
                <h4 className="text-xs font-semibold text-primary flex items-center gap-2">
                  <div className="i-ph:envelope text-sm text-[#D9F26A]" />
                  Email Provider
                </h4>
                <p className="text-xs text-muted">
                  Configure an email provider to send notifications and auto-replies.
                </p>

                <div className="space-y-3">
                  <select
                    value={settings.emailProvider?.provider || ''}
                    onChange={(e) =>
                      updateSettings('emailProvider', {
                        ...(settings.emailProvider || { apiKey: '', fromEmail: '', fromName: '' }),
                        provider: e.target.value as EmailProviderType,
                      })
                    }
                    className="w-full text-xs px-3 py-2 rounded-md bg-raised border border-hairline text-primary"
                  >
                    <option value="">Select a provider...</option>
                    {EMAIL_PROVIDERS.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>

                  {settings.emailProvider?.provider && (
                    <>
                      <input
                        type="password"
                        placeholder="API Key"
                        value={settings.emailProvider?.apiKey || ''}
                        onChange={(e) =>
                          updateSettings('emailProvider', {
                            ...settings.emailProvider!,
                            apiKey: e.target.value,
                          })
                        }
                        className="w-full text-xs px-3 py-2 rounded-md bg-raised border border-hairline text-primary placeholder:text-muted"
                      />

                      {selectedProvider?.needsDomain && (
                        <input
                          type="text"
                          placeholder="Domain (e.g., mg.yourdomain.com)"
                          value={settings.emailProvider?.domain || ''}
                          onChange={(e) =>
                            updateSettings('emailProvider', {
                              ...settings.emailProvider!,
                              domain: e.target.value,
                            })
                          }
                          className="w-full text-xs px-3 py-2 rounded-md bg-raised border border-hairline text-primary placeholder:text-muted"
                        />
                      )}

                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="email"
                          placeholder="From Email"
                          value={settings.emailProvider?.fromEmail || ''}
                          onChange={(e) =>
                            updateSettings('emailProvider', {
                              ...settings.emailProvider!,
                              fromEmail: e.target.value,
                            })
                          }
                          className="text-xs px-3 py-2 rounded-md bg-raised border border-hairline text-primary placeholder:text-muted"
                        />
                        <input
                          type="text"
                          placeholder="From Name"
                          value={settings.emailProvider?.fromName || ''}
                          onChange={(e) =>
                            updateSettings('emailProvider', {
                              ...settings.emailProvider!,
                              fromName: e.target.value,
                            })
                          }
                          className="text-xs px-3 py-2 rounded-md bg-raised border border-hairline text-primary placeholder:text-muted"
                        />
                      </div>
                    </>
                  )}
                </div>
              </section>

              {/* Admin Notification */}
              <section className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-semibold text-primary flex items-center gap-2">
                    <div className="i-ph:bell text-sm text-[#D9F26A]" />
                    Admin Notification
                  </h4>
                  <button
                    onClick={() =>
                      updateSettings('adminNotification', {
                        ...(settings.adminNotification || { to: '', subject: '', message: '' }),
                        enabled: !settings.adminNotification?.enabled,
                      })
                    }
                    className={classNames(
                      'w-8 h-5 rounded-full transition-colors relative',
                      settings.adminNotification?.enabled ? 'bg-[#D9F26A]' : 'bg-sunken',
                    )}
                  >
                    <div
                      className={classNames(
                        'absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform',
                        settings.adminNotification?.enabled ? 'translate-x-3.5' : 'translate-x-0.5',
                      )}
                    />
                  </button>
                </div>
                <p className="text-xs text-muted">Get notified when someone submits this form.</p>

                {settings.adminNotification?.enabled && (
                  <div className="space-y-2">
                    <input
                      type="email"
                      placeholder="Notification email"
                      value={settings.adminNotification?.to || ''}
                      onChange={(e) =>
                        updateSettings('adminNotification', {
                          ...settings.adminNotification!,
                          to: e.target.value,
                        })
                      }
                      className="w-full text-xs px-3 py-2 rounded-md bg-raised border border-hairline text-primary placeholder:text-muted"
                    />
                    <input
                      type="text"
                      placeholder="Email subject"
                      value={settings.adminNotification?.subject || ''}
                      onChange={(e) =>
                        updateSettings('adminNotification', {
                          ...settings.adminNotification!,
                          subject: e.target.value,
                        })
                      }
                      className="w-full text-xs px-3 py-2 rounded-md bg-raised border border-hairline text-primary placeholder:text-muted"
                    />
                    <textarea
                      placeholder="Message template (use {{field}} for values)"
                      value={settings.adminNotification?.message || ''}
                      onChange={(e) =>
                        updateSettings('adminNotification', {
                          ...settings.adminNotification!,
                          message: e.target.value,
                        })
                      }
                      rows={3}
                      className="w-full text-xs px-3 py-2 rounded-md bg-raised border border-hairline text-primary placeholder:text-muted resize-none"
                    />
                  </div>
                )}
              </section>

              {/* Auto Reply */}
              <section className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-semibold text-primary flex items-center gap-2">
                    <div className="i-ph:arrow-u-up-left text-sm text-[#D9F26A]" />
                    Auto Reply
                  </h4>
                  <button
                    onClick={() =>
                      updateSettings('autoReply', {
                        ...(settings.autoReply || { replyToField: 'email', subject: '', message: '' }),
                        enabled: !settings.autoReply?.enabled,
                      })
                    }
                    className={classNames(
                      'w-8 h-5 rounded-full transition-colors relative',
                      settings.autoReply?.enabled ? 'bg-[#D9F26A]' : 'bg-sunken',
                    )}
                  >
                    <div
                      className={classNames(
                        'absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform',
                        settings.autoReply?.enabled ? 'translate-x-3.5' : 'translate-x-0.5',
                      )}
                    />
                  </button>
                </div>
                <p className="text-xs text-muted">Automatically reply to the person who submitted the form.</p>

                {settings.autoReply?.enabled && (
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder="Email field name (e.g., email)"
                      value={settings.autoReply?.replyToField || 'email'}
                      onChange={(e) =>
                        updateSettings('autoReply', {
                          ...settings.autoReply!,
                          replyToField: e.target.value,
                        })
                      }
                      className="w-full text-xs px-3 py-2 rounded-md bg-raised border border-hairline text-primary placeholder:text-muted"
                    />
                    <input
                      type="text"
                      placeholder="Reply subject"
                      value={settings.autoReply?.subject || ''}
                      onChange={(e) =>
                        updateSettings('autoReply', {
                          ...settings.autoReply!,
                          subject: e.target.value,
                        })
                      }
                      className="w-full text-xs px-3 py-2 rounded-md bg-raised border border-hairline text-primary placeholder:text-muted"
                    />
                    <textarea
                      placeholder="Reply message (use {{field}} for values)"
                      value={settings.autoReply?.message || ''}
                      onChange={(e) =>
                        updateSettings('autoReply', {
                          ...settings.autoReply!,
                          message: e.target.value,
                        })
                      }
                      rows={3}
                      className="w-full text-xs px-3 py-2 rounded-md bg-raised border border-hairline text-primary placeholder:text-muted resize-none"
                    />
                  </div>
                )}
              </section>

              {/* Webhook */}
              <section className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-semibold text-primary flex items-center gap-2">
                    <div className="i-ph:webhooks-logo text-sm text-[#D9F26A]" />
                    Webhook
                  </h4>
                  <button
                    onClick={() =>
                      updateSettings('webhook', {
                        ...(settings.webhook || { url: '' }),
                        enabled: !settings.webhook?.enabled,
                      })
                    }
                    className={classNames(
                      'w-8 h-5 rounded-full transition-colors relative',
                      settings.webhook?.enabled ? 'bg-[#D9F26A]' : 'bg-sunken',
                    )}
                  >
                    <div
                      className={classNames(
                        'absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform',
                        settings.webhook?.enabled ? 'translate-x-3.5' : 'translate-x-0.5',
                      )}
                    />
                  </button>
                </div>
                <p className="text-xs text-muted">Send form data to an external URL (Zapier, Make, etc).</p>

                {settings.webhook?.enabled && (
                  <div className="space-y-2">
                    <input
                      type="url"
                      placeholder="Webhook URL"
                      value={settings.webhook?.url || ''}
                      onChange={(e) =>
                        updateSettings('webhook', {
                          ...settings.webhook!,
                          url: e.target.value,
                        })
                      }
                      className="w-full text-xs px-3 py-2 rounded-md bg-raised border border-hairline text-primary placeholder:text-muted"
                    />
                  </div>
                )}
              </section>

              {/* Save Button */}
              <div className="pt-4 border-t border-hairline">
                <button
                  onClick={handleSaveSettings}
                  disabled={savingSettings}
                  className={classNames(
                    'w-full',
                    settingsSaved
                      ? 'flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-xs font-medium transition-colors bg-green-500/20 text-green-400'
                      : 'ui-btn-accent',
                    savingSettings && 'opacity-50 cursor-not-allowed',
                  )}
                >
                  {savingSettings ? (
                    <>
                      <div className="i-svg-spinners:90-ring-with-bg text-sm" />
                      Saving...
                    </>
                  ) : settingsSaved ? (
                    <>
                      <div className="i-ph:check text-sm" />
                      Saved
                    </>
                  ) : (
                    <>
                      <div className="i-ph:floppy-disk text-sm" />
                      Save Settings
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <ConfirmationDialog
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => {
          if (deleteConfirm) {
            handleDelete(deleteConfirm);
          }
        }}
        title="Delete this submission?"
        description="This can't be undone."
        confirmLabel="Delete"
        variant="destructive"
      />
    </div>
  );
}
